import React, { useEffect, useState, useRef } from 'react';
import { useDemo } from '../context/DemoContext';

/**
 * DemoArrow — visual pointer that highlights the button the demo user should click next.
 *
 * Reads the current step's `target_selector` from the demo config and finds that
 * element on the page. Renders a fixed-position bouncing arrow + soft glow halo
 * around the element. Updates on step change, scroll, resize, and DOM mutations.
 *
 * Hidden if no target is found (e.g. user is on a different page than the step suggests).
 */
const DemoArrow = () => {
  const { isDemo, active, currentStep } = useDemo();
  const [box, setBox] = useState(null);   // { left, top, width, height } in viewport coords
  const rafRef = useRef(null);
  const targetRef = useRef(null);
  const targetSelector = currentStep?.target_selector || null;

  useEffect(() => {
    if (!isDemo || !active || !targetSelector) {
      setBox(null);
      return;
    }

    let cancelled = false;

    const updateBox = () => {
      if (cancelled) return;

      // If any modal/dialog is open, hide the arrow so it doesn't overlap popups.
      // Detect: shadcn Dialog (role=dialog), our custom z-50/z-[60]/z-[100] overlays,
      // and the demo's own welcome/expired modals.
      const modalOpen =
        document.querySelector('[role="dialog"][data-state="open"]') ||
        document.querySelector('[role="alertdialog"][data-state="open"]') ||
        document.querySelector('[data-testid="demo-welcome-modal"]') ||
        document.querySelector('[data-testid="demo-expired-modal"]') ||
        // Generic full-screen overlay heuristic — fixed positioned overlay covering most of the viewport.
        Array.from(document.querySelectorAll('.fixed.inset-0')).some((n) => {
          const cs = window.getComputedStyle(n);
          return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0.1;
        });
      if (modalOpen) {
        setBox((prev) => (prev === null ? prev : null));
        return;
      }

      let el = targetRef.current;
      if (!el || !document.body.contains(el)) {
        try {
          el = document.querySelector(targetSelector);
          targetRef.current = el;
        } catch {
          el = null;
        }
      }
      if (!el) {
        setBox((prev) => (prev === null ? prev : null));
        return;
      }
      const r = el.getBoundingClientRect();
      // Skip if element is offscreen or hidden
      if (r.width === 0 || r.height === 0) {
        setBox(null);
        return;
      }
      setBox({ left: r.left, top: r.top, width: r.width, height: r.height });
    };

    const tick = () => {
      updateBox();
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      targetRef.current = null;
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [isDemo, active, targetSelector]);

  if (!isDemo || !active || !box) return null;

  // Position arrow to the LEFT of the target (or above if target is in the top-right corner).
  const targetCenterX = box.left + box.width / 2;
  const isInRightHalf = targetCenterX > window.innerWidth / 2;

  // Halo
  const halo = {
    position: 'fixed',
    left: box.left - 8,
    top: box.top - 8,
    width: box.width + 16,
    height: box.height + 16,
    borderRadius: 12,
    pointerEvents: 'none',
    boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.55), 0 0 24px 8px rgba(249, 115, 22, 0.4)',
    animation: 'demoHaloPulse 1.4s ease-in-out infinite',
    zIndex: 90,
  };

  // Arrow position
  const arrowSize = 56;
  let arrowStyle;
  if (isInRightHalf) {
    // Arrow on the LEFT pointing RIGHT toward the button
    arrowStyle = {
      position: 'fixed',
      left: box.left - arrowSize - 16,
      top: box.top + box.height / 2 - arrowSize / 2,
      width: arrowSize,
      height: arrowSize,
      pointerEvents: 'none',
      animation: 'demoArrowBounceX 0.8s ease-in-out infinite',
      zIndex: 91,
    };
  } else {
    // Arrow on the RIGHT pointing LEFT toward the button
    arrowStyle = {
      position: 'fixed',
      left: box.left + box.width + 16,
      top: box.top + box.height / 2 - arrowSize / 2,
      width: arrowSize,
      height: arrowSize,
      pointerEvents: 'none',
      animation: 'demoArrowBounceXReverse 0.8s ease-in-out infinite',
      zIndex: 91,
      transform: 'scaleX(-1)',
    };
  }

  // Inline keyframes (single mount — fine to inject once)
  const keyframes = `
    @keyframes demoHaloPulse {
      0%, 100% { box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.55), 0 0 24px 8px rgba(249, 115, 22, 0.4); }
      50% { box-shadow: 0 0 0 6px rgba(249, 115, 22, 0.8), 0 0 36px 14px rgba(249, 115, 22, 0.55); }
    }
    @keyframes demoArrowBounceX {
      0%, 100% { transform: translateX(0); }
      50% { transform: translateX(8px); }
    }
    @keyframes demoArrowBounceXReverse {
      0%, 100% { transform: scaleX(-1) translateX(0); }
      50% { transform: scaleX(-1) translateX(8px); }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div data-testid="demo-arrow-halo" style={halo} />
      <div data-testid="demo-arrow" style={arrowStyle} aria-hidden="true">
        <svg viewBox="0 0 64 64" width={arrowSize} height={arrowSize} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="demoArrowShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.35" />
            </filter>
          </defs>
          <g filter="url(#demoArrowShadow)">
            <path
              d="M4 32 L40 32 M28 18 L42 32 L28 46"
              stroke="#f97316"
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M4 32 L40 32 M28 18 L42 32 L28 46"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </svg>
      </div>
    </>
  );
};

export default DemoArrow;
